import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { clearOtpCooldown, LOGIN_OTP_FLOW_KEY } from '../hooks/useOtpCooldown';

/**
 * Store del flujo de login pre-auth, persistido en `sessionStorage` (DEC-RBAC-055).
 *
 * Antes el flujo de login era una máquina de estado en `useState` LOCAL de `LoginForm`: al saltar
 * a las rutas separadas `/login/mfa-challenge` y `/login/new-password`, `LoginForm` se desmontaba y
 * cualquier "volver" remontaba vacío → el usuario reescribía todo. Mover el estado a un store
 * persistido habilita: (a) "atrás" entre rutas sin reiniciar, (b) el flujo sobrevive un refresh,
 * (c) saca el `session` de Cognito de la URL (queda scope-pestaña en sessionStorage, no en el
 * history). Se limpia (reset() — borra el store Y el cooldown de login) en los 3 casos del spec
 * §5: login exitoso, "Cancelar" explícito, y al salir del área pre-auth (cleanup del layout /login).
 *
 * Seguridad: `session` es la session corta de Cognito (TTL del challenge). sessionStorage es
 * scope-pestaña y se limpia al cerrar; riesgo XSS equivalente a tenerlo en memoria/URL, mejor que
 * la URL (que queda en el history). Ver ARCHITECTURE.md §amenazas.
 */

export type LoginStep = 'email' | 'pick' | 'password' | 'mfa' | 'new-password';

/** Sub-challenge real del login (display); el answer hacia Cognito siempre va por ANSWER. */
// DEC-RBAC-084: SMS_OTP es un 3er sub-challenge de MFA (código que llega por SMS), tratado igual que
// EMAIL_OTP en el ruteo (paso 'mfa').
export type LoginChallengeType =
    | 'EMAIL_OTP'
    | 'SMS_OTP'
    | 'SOFTWARE_TOKEN_MFA'
    | 'NEW_PASSWORD_REQUIRED';

export interface LoginTenantOption {
    tenantId: string;
    displayName: string;
}

interface LoginFlowState {
    /** Paso actual del flujo (resume aquí al montar/refrescar). */
    step: LoginStep;
    /** Email ingresado (paso email). Vacío = flujo no arrancado. */
    email: string;
    /** Tenant elegido (single-tenant directo o tras el picker). */
    tenantId: string | null;
    /** Nombre visible del tenant elegido (para el copy "Ingresando a …"). */
    tenantName: string | null;
    /** Candidatos del picker (multipool, 2+). */
    tenants: LoginTenantOption[];
    /** Session de Cognito del challenge en curso (mfa/new-password). */
    session: string | null;
    /** Sub-challenge real del login. */
    challengeType: LoginChallengeType | null;

    /** Setea el email y avanza según los tenants resueltos. */
    setEmail: (email: string) => void;
    /**
     * Recibe los tenants resueltos por `resolveTenants`:
     *  - 1 tenant  → avanza directo a 'password' con ese tenant.
     *  - 2+ tenants → guarda candidatos y va a 'pick'.
     */
    setTenants: (tenants: LoginTenantOption[]) => void;
    /** El usuario eligió un tenant del picker → paso 'password'. */
    pickTenant: (tenant: LoginTenantOption) => void;
    /** El login devolvió un challenge → guarda session/tipo y rutea a 'mfa' o 'new-password'. */
    setChallenge: (input: { session: string; challengeType: LoginChallengeType }) => void;
    /** Refresca la session del challenge en curso (reenvío de OTP) sin cambiar de paso. */
    setSession: (session: string) => void;
    /** Vuelve un paso atrás preservando lo cargado (mfa/new-password → password → email). */
    back: () => void;
    /** Limpia TODO el flujo (store + cooldown de login). Login OK, "Cancelar" o salir del área. */
    reset: () => void;
}

const INITIAL: Pick<
    LoginFlowState,
    'step' | 'email' | 'tenantId' | 'tenantName' | 'tenants' | 'session' | 'challengeType'
> = {
    step: 'email',
    email: '',
    tenantId: null,
    tenantName: null,
    tenants: [],
    session: null,
    challengeType: null,
};

export const useLoginFlowStore = create<LoginFlowState>()(
    persist(
        (set, get) => ({
            ...INITIAL,

            setEmail: (email) => {
                set({ email });
            },

            setTenants: (tenants) => {
                if (tenants.length === 1) {
                    const only = tenants[0];
                    set({
                        tenants,
                        tenantId: only.tenantId,
                        tenantName: only.displayName,
                        step: 'password',
                    });
                    return;
                }
                // 0 tenants → el caller muestra el mensaje genérico (anti-enum) y NO avanza el step.
                // 2+ tenants → picker.
                set({
                    tenants,
                    step: tenants.length >= 2 ? 'pick' : 'email',
                });
            },

            pickTenant: (tenant) => {
                set({
                    tenantId: tenant.tenantId,
                    tenantName: tenant.displayName,
                    step: 'password',
                });
            },

            setChallenge: ({ session, challengeType }) => {
                set({
                    session,
                    challengeType,
                    step: challengeType === 'NEW_PASSWORD_REQUIRED' ? 'new-password' : 'mfa',
                });
            },

            setSession: (session) => {
                set({ session });
            },

            back: () => {
                const { step, tenants } = get();
                switch (step) {
                    case 'mfa':
                    case 'new-password':
                        // Desde el challenge volvemos al paso password preservando email/tenant.
                        // La session vieja se descarta (al reentrar a password se re-loguea).
                        set({ step: 'password', session: null, challengeType: null });
                        return;
                    case 'password':
                        // Si el email era multipool volvemos al picker; si no, al email.
                        set(
                            tenants.length >= 2
                                ? { step: 'pick', tenantId: null, tenantName: null }
                                : { step: 'email', tenantId: null, tenantName: null },
                        );
                        return;
                    case 'pick':
                        set({ step: 'email', tenants: [], tenantId: null, tenantName: null });
                        return;
                    default:
                        return;
                }
            },

            reset: () => {
                // Limpia TODO el flujo: el estado del store (persistido en fiado.loginFlow) Y el
                // cooldown de OTP del login (fiado.otpCooldown.login) — así no queda un cooldown
                // stale para el próximo login en la misma pestaña. Cubre los 3 casos de limpieza
                // del spec §5: login exitoso, "Cancelar" explícito, y salir del área pre-auth.
                clearOtpCooldown(LOGIN_OTP_FLOW_KEY);
                set({ ...INITIAL });
            },
        }),
        {
            name: 'fiado.loginFlow',
            storage: createJSONStorage(() => sessionStorage),
        },
    ),
);
